/**
 * Projects CRUD against the Projects tab.
 *
 * Projects are identified by the id in column A. Renaming a project rewrites
 * its name here and on every task row that points at it, which is what keeps
 * column B of the TASKS tab readable to a human opening the spreadsheet.
 */
import { config } from "../config";
import { sheets } from "../lib/sheets";
import {
  PROJECTS_FIRST_DATA_ROW,
  projectRowRange,
  projectToRow,
  projectsReadRange,
  rowToProject,
} from "../lib/schema";
import type { Project, ProjectDraft } from "../types";

const tab = () => config.projectsTab;

export function newProjectId(): string {
  return `p-${crypto.randomUUID().slice(0, 8)}`;
}

export async function listProjects(token: string): Promise<Project[]> {
  const rows = await sheets.read(projectsReadRange(tab()), token);
  return rows
    .map((row, index) => rowToProject(row, index + PROJECTS_FIRST_DATA_ROW))
    .filter((p) => p.id !== "" && p.name !== "");
}

async function resolveRow(id: string, token: string): Promise<Project> {
  const match = (await listProjects(token)).find((p) => p.id === id);
  if (!match) {
    throw new Error("That project is no longer in the sheet. Refresh to reload.");
  }
  return match;
}

export async function createProject(
  draft: ProjectDraft,
  token: string
): Promise<Project> {
  const project: Project = { ...draft, id: newProjectId(), sheetRow: -1 };
  const row = await sheets.append(
    projectsReadRange(tab()),
    projectToRow(project),
    token
  );
  return { ...project, sheetRow: row };
}

export async function updateProject(
  project: Project,
  token: string
): Promise<Project> {
  const current = await resolveRow(project.id, token);
  const next = { ...project, sheetRow: current.sheetRow };
  await sheets.batchWrite(
    [{ range: projectRowRange(tab(), next.sheetRow), values: projectToRow(next) }],
    token
  );
  return next;
}
